// Carousel fonts.
//
// One font family for all the text on the slides (titles, body, hashtags,
// labels). They are loaded from Google Fonts for now (self-hosting is planned
// for later). The canvas draws with whatever font is ready at that moment, so
// drawing and the PDF export must wait for loadCarouselFonts() first, or the
// slides come out in a fallback font.
//
// Each entry keeps a title and a body font so a layout could still pair two
// fonts later; today both are the same family.
// bodyWeight / subtitleWeight: weight of the body text and the hook subtitle.
// Poppins and Fraunces look heavier than Inter, so they use Light (300).

export const FONT_PAIRS = {
  inter: {
    name: "Inter",
    title: "'Inter', sans-serif",
    body: "'Inter', sans-serif",
    bodyWeight: 400,
    subtitleWeight: 500,
  },
  poppins: {
    name: "Poppins",
    title: "'Poppins', sans-serif",
    body: "'Poppins', sans-serif",
    bodyWeight: 300,
    subtitleWeight: 400,
  },
  fraunces: {
    name: "Fraunces",
    title: "'Fraunces', serif",
    body: "'Fraunces', serif",
    bodyWeight: 300,
    subtitleWeight: 400,
  },
};

export const DEFAULT_FONT_PAIR_ID = "inter";

// Text sizes in canvas pixels (the slide is 1080 x 1350). These are the real
// sizes in the Clean layout; Bold and Editorial scale titles on top.
//   h1 = hook title (first slide)     h2 = content slide titles
//   cta = last slide title            body = body text and hook subtitle
//   hashtags = last slide pills
export const DEFAULT_SIZES = {
  h1: 66,
  h2: 40,
  cta: 40,
  body: 34,
  hashtags: 28,
};

// Slider limits for each size in the Font settings panel.
export const SIZE_RANGES = {
  h1: { min: 44, max: 110 },
  h2: { min: 28, max: 72 },
  cta: { min: 28, max: 80 },
  body: { min: 22, max: 48 },
  hashtags: { min: 20, max: 40 },
};

export const resolveFontPair = (id) =>
  FONT_PAIRS[id] || FONT_PAIRS[DEFAULT_FONT_PAIR_ID];

const WEIGHTS = "300;400;500;600;700;800";
const GOOGLE_FONTS_URL =
  "https://fonts.googleapis.com/css2" +
  `?family=Inter:wght@${WEIGHTS}` +
  `&family=Poppins:wght@${WEIGHTS}` +
  `&family=Fraunces:wght@${WEIGHTS}` +
  "&display=swap";

// Every weight the layouts draw with, for each family.
// document.fonts.load() downloads each one.
const FACES = ["Inter", "Poppins", "Fraunces"].flatMap((family) =>
  [300, 400, 500, 600, 700, 800].map((w) => `${w} 40px ${family}`)
);

// Never wait longer than this: a slow or blocked Google Fonts must not
// freeze the editor.
const MAX_WAIT_MS = 3000;

let loading = null;

const addStylesheet = () =>
  new Promise((resolve) => {
    const existing = document.querySelector("link[data-carousel-fonts]");
    if (existing) return resolve();
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = GOOGLE_FONTS_URL;
    link.setAttribute("data-carousel-fonts", "");
    link.onload = () => resolve();
    link.onerror = () => resolve();
    document.head.appendChild(link);
  });

// Resolves when the fonts are ready, or after MAX_WAIT_MS. Never rejects.
// Safe to call many times: the fonts are requested only once.
export function loadCarouselFonts() {
  if (loading) return loading;
  if (typeof document === "undefined" || !document.fonts) {
    return Promise.resolve();
  }
  const work = addStylesheet().then(() =>
    Promise.all(
      FACES.map((face) => document.fonts.load(face).catch(() => null))
    )
  );
  const timeout = new Promise((resolve) => setTimeout(resolve, MAX_WAIT_MS));
  loading = Promise.race([work, timeout]).then(() => undefined);
  return loading;
}